import React, { useState } from "react";
import { MPSharedTable, MPSharedDeleteModal, MPSharedHeader } from "../Shared";
import { useSearchQuery, usePagination, useDelete } from "../../../hooks";
import { useGetUserGroupsQuery, useDeleteUserGroupMutation } from "../services";
import { UserGroupForm } from "./form";
import { UserGroupCsvUploadForm } from "./csvUploadForm";
import { Link } from "react-router-dom";
import { getLocalizedString } from "../../../shared/translation";
import { getUserGroupRoleNames } from "./tableData";

export const UserGroupsContainer = () => {
  const [idToDelete, setIdToDelete] = useState(null);
  const [idToEditOrCsvUpload, setIdToEditOrCsvUpload] = useState(null);
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showCsvUploadModal, setShowCsvUploadModal] = useState(false);
  const [showCsvUploadForm, setShowCsvUploadForm] = useState(false);

  const { page, setPage, perPage, pagination } = usePagination();

  const { setSimpleSearch, q } = useSearchQuery({
    page,
    setPage,
    simpleSearchKey: "name_cont",
  });

  const queryParams = {
    page: page,
    per_page: perPage,
    order_by: "created_at",
    order_dir: "desc",
  };

  const {
    data = {
      total_count: 0,
      data: [],
    },
    error,
    isFetching,
    isSuccess,
  } = useGetUserGroupsQuery({
    ...queryParams,
    q,
  });

  const {
    deleteItem: deleteUserGroup,
    deleteErrorMsg,
    setDeleteErrorMsg,
  } = useDelete({
    deleteMutation: useDeleteUserGroupMutation,
    closeModal: () => setShowDeleteWarning(false),
  });

  const headers = [
    {
      label: getLocalizedString("name", "Name"),
      key: "name",
      className: "name",
    },
    {
      label: getLocalizedString("role", "Role"),
      nestedValue: true,
      className: "role",
      getNestedValue: getUserGroupRoleNames,
    },
    {
      label: getLocalizedString("users", "Users"),
      nestedValue: true,
      className: "users text-center",
      getNestedValue: ({ resources_count, id, name }) => {
        return (
          <>
            {resources_count > 0 ? (
              <Link
                to={`/management_portal/users_roles/users?user_group_id=${id}&name=${name}`}
                style={{ textDecoration: "none" }}
              >
                {resources_count}
              </Link>
            ) : (
              <span>0</span>
            )}
          </>
        );
      },
    },
    {
      label: getLocalizedString("added_on", "Added On"),
      key: "created_at",
      className: "date",
    },
    {
      label: getLocalizedString("added_by", "Added By"),
      key: "created_by",
      className: "added-by",
    },
  ];

  const onEdit = (data) => {
    setShowForm(true);
    setIdToEditOrCsvUpload(data.id);
  };

  const onUploadCsv = (data) => {
    setShowCsvUploadModal(true);
    setIdToEditOrCsvUpload(data.id);
  };

  const onDelete = (data) => {
    setShowDeleteWarning(true);
    setIdToDelete(data.id);
  };

  const onCreate = () => {
    setShowForm(true);
    setIdToEditOrCsvUpload(null);
  };

  const uploadCsvHandler = () => {
    setShowCsvUploadForm(true);
  };

  const getDeleteItemName = (id) => {
    let name = "";
    const index = data?.data.findIndex((item) => item.id === id);

    if (index >= 0) {
      name = data.data[index].name;
    }
    return name;
  };

  // Function to check if the group is deletable
  // A group is not deletable if it is a system group (by_system is true)
  const isDeletable = (data, id) => {
    const deletable = data?.data.some((item) => {
      if (item.id === id) return item.created_by === "System" ? false : true;
    });
    return deletable;
  };

  return (
    <>
      <article className="main-container">
        <MPSharedHeader
          groupName="group"
          resourceName="Groups"
          createNewHandler={onCreate}
          heading={getLocalizedString("groups", "Groups")}
          handleSearchKey={setSimpleSearch}
          simpleSearchPlaceholder={getLocalizedString("search_by_name", "Search by Name")}
          uploadCsvHandler={uploadCsvHandler}
        />

        <MPSharedTable
          resourceName="Groups"
          data={data?.data || []}
          error={error}
          isLoading={isFetching}
          isSuccess={isSuccess}
          headers={headers}
          onEdit={onEdit}
          onDelete={onDelete}
          onUploadCsv={onUploadCsv}
          pagination={{ ...pagination, count: data.total_count }}
          skipClientId
          className="user-groups"
          auditResource="UserGroup"
          auditKey="name"
        />
      </article>

      {showCsvUploadModal && (
        <UserGroupCsvUploadForm
          idToEditOrCsvUpload={idToEditOrCsvUpload}
          onClose={() => setShowCsvUploadModal(false)}
        />
      )}

      {showForm && (
        <UserGroupForm
          idToEditOrCsvUpload={idToEditOrCsvUpload}
          onClose={() => setShowForm(false)}
        />
      )}

      {showCsvUploadForm && <UserGroupCsvUploadForm onClose={() => setShowCsvUploadForm(false)} />}

      <MPSharedDeleteModal
        show={showDeleteWarning}
        entityName="User Group"
        error={isDeletable(data, idToDelete) ? deleteErrorMsg : "Cannot delete system group"}
        itemName={getDeleteItemName(idToDelete)}
        onHide={() => {
          setDeleteErrorMsg("");
          setShowDeleteWarning(false);
        }}
        onDelete={() => deleteUserGroup({ id: idToDelete })}
      />
    </>
  );
};
